import React, { useState, useCallback } from 'react';
import { AudioRecorderComponent } from '../audioRecorder/AudioRecorder.component';
import CameraCapture from '../CameraCapture.component';
import { FileUploadButton } from '../FileUploadButton.component';
import URLCapture from '../URLCapture.component';
import { useFluidFormClient } from '../../fluidForm.client';
import { useFluidForm, useFormInstance, useFormToast } from './FormContext';
import { useFormInstanceDispatch, useFormInstanceSelect } from './FormInstance.service';
import { FeatureFlagWrapper } from '../../featureFlags/FeatureFlagWrapper';
import { FeatureToogles } from '../../../shared';
import WhatsAppWindow from '../WhatsAppWindow.component';
export const FormInputModes = ({ inputModes, layout = 'horizontal', spacing = 'md', background = 'white', alignTooltips, getCurrentData }) => {
    const [activeInputModeButtons, setActiveInputModeButtons] = useState([]);
    const spacingClasses = {
        sm: { horizontal: 'space-x-2', vertical: 'space-y-2' },
        md: { horizontal: 'space-x-3', vertical: 'space-y-3' },
        lg: { horizontal: 'space-x-6', vertical: 'space-y-6' },
    };
    inputModes = inputModes || [
        "speech",
        "streamSpeech",
        "fileUpload",
        "camera",
        "urlCapture"
    ];
    const client = useFluidFormClient();
    const instanceId = useFormInstance();
    const form = useFluidForm();
    const { addToast } = useFormToast();
    const formKey = useFormInstanceSelect((state) => state.formKey);
    const submissionId = useFormInstanceSelect((state) => state.submissionId);
    const dispatch = useFormInstanceDispatch(instanceId);
    const sendChatMessage = (text) => {
        dispatch('chatbotService.appendMessage', { text, role: "assistant" });
    };
    const handleAudio = async (audio) => {
        try {
            const currentData = getCurrentData?.() || form.getFormSubmission();
            sendChatMessage("Processing audio...");
            const res = await client.processAudio(formKey, audio, submissionId, currentData);
            client.countMetric(submissionId, "speech_mode", 1);
            sendChatMessage(res.message);
            dispatch("mergeSubmissionData", res.data);
        }
        catch (error) {
            const errorMsg = error.message;
            addToast('Failed to process audio', 'error', {
                'kind': 'Retry',
                'onClick': () => handleAudio(audio)
            });
        }
    };
    const handleStreamSpeechResult = useCallback((result) => {
        const currentData = getCurrentData?.() || form.getFormSubmission();
        if (result?.message) {
            sendChatMessage(result.message);
        }
        if (result?.data) {
            dispatch("mergeSubmissionData", result.data);
        }
    }, [dispatch, form]);
    const handlePicture = async (picture) => {
        try {
            const currentData = getCurrentData?.() || form.getFormSubmission();
            sendChatMessage("Processing image...");
            const res = await client.processImage(formKey, picture, submissionId, currentData);
            client.countMetric(submissionId, "camera_mode", 1);
            sendChatMessage(res.message);
            dispatch("mergeSubmissionData", res.data);
        }
        catch (error) {
            const errorMsg = error.message;
            addToast('Failed to process image', 'error', {
                'kind': 'Retry',
                'onClick': () => handlePicture(picture)
            });
        }
    };
    const handleFileUpload = async (file) => {
        try {
            const currentData = getCurrentData?.() || form.getFormSubmission();
            sendChatMessage("Processing file...");
            //If the file is image use processImage
            if (file.type.startsWith('image/')) {
                const res = await client.processImage(formKey, file, submissionId, currentData);
                dispatch("mergeSubmissionData", res.data);
                return;
            }
            client.countMetric(submissionId, "file_upload_mode", 1);
            const response = await client.processPDF(formKey, file, submissionId, currentData);
            sendChatMessage(response.message);
            //configService.mergeSubmissionData(response.data);
            dispatch("mergeSubmissionData", response.data);
        }
        catch (error) {
            console.error('Error handling file upload:', error);
            const errorMsg = error.message;
            addToast('Failed to process file', 'error', {
                'kind': 'Retry',
                'onClick': () => handleFileUpload(file)
            });
        }
    };
    const handleURL = async (url) => {
        try {
            const currentData = getCurrentData?.() || form.getFormSubmission();
            // Capture the URL as an image
            const imageBlob = await client.captureUrl(url);
            // Convert blob to file
            const file = new File([imageBlob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
            // Process using the existing PDF processing endpoint (which supports images)
            const data = await client.processPDF(formKey, file, submissionId, currentData);
            dispatch("mergeSubmissionData", data);
        }
        catch (error) {
            addToast('Failed to process URL', 'error', {
                'kind': 'Retry',
                'onClick': () => handleURL(url)
            });
        }
    };
    const currentSpacing = spacingClasses[spacing];
    const layoutClasses = layout === 'horizontal'
        ? `flex justify-start items-center ${currentSpacing.horizontal}`
        : `flex flex-col ${currentSpacing.vertical}`;
    const handleOnActive = (inputMode, isActive) => {
        setActiveInputModeButtons(prev => {
            if (isActive) {
                return [...prev, inputMode];
            }
            else {
                return prev.filter(mode => mode !== inputMode);
            }
        });
    };
    const shouldShowButton = (inputMode) => {
        return inputModes.includes(inputMode) && !activeInputModeButtons.some(mode => mode !== inputMode);
    };
    return (React.createElement("div", { className: layoutClasses },
        inputModes.includes('speech') && (React.createElement(AudioRecorderComponent, { onFinalAudio: handleAudio, background: background, alignTooltip: alignTooltips, onActive: (isActive) => handleOnActive('speech', isActive) })),
        React.createElement("div", { className: `flex items-center ${currentSpacing.horizontal}` },
            shouldShowButton('fileUpload') && (React.createElement(FileUploadButton, { onFileSelect: handleFileUpload, background: background })),
            shouldShowButton('camera') && (React.createElement(CameraCapture, { onPicture: handlePicture, background: background })),
            React.createElement(FeatureFlagWrapper, { flagName: FeatureToogles.UrlCapture },
                React.createElement(URLCapture, { onURLCapture: handleURL, background: background })),
            shouldShowButton('whatsapp') && (React.createElement(WhatsAppWindow, { background: background })))));
};
